import {
  BRAND_LIST,
  ContentGroup,
  GAeCommerceItem,
  PageType,
  PageViewBaseOption,
  ProductGAInfoType,
  SiteType
} from "@type/index";
import store from "@redux/store";
import { getSub } from "@util/helpers";
import {
  checkPreviousPageExists,
  getLoginState,
  transformBrandForGoogleAnalytics
} from "./utils";

const TAG_LIST_SPLIT_LIMIT = 500;

export const EVENT = "trackPageView";
export const TRACK_EVENT = "trackEvent";
export const TRACK_EVENT_SEARCH = "trackEventSearch";
export const ADD_TO_WISHLIST = "add_to_wishlist";
export const REMOVE_FROM_WISHLIST = "remove_from_wishlist";
export const SELECT_ITEM = "select_item";
export const SELECT_STYLING = "select_styling";
export const ADD_TO_STYLING_WISHLIST = "add_to_styling_wishlist";
export const REMOVE_FROM_STYLING_WISHLIST = "remove_from_styling_wishlist";
export const CLICK_RECOMMENDED_USER = "click_recommended_user";
export const VIEW_STYLING = "view_styling";
export const VIEW_ITEM_LIST = "view_item_list";
export const APPLY_FILTER = "apply_filter";

export const getPageViewBaseOption = (
  pageType: PageType,
  pageTitle: string,
  content_group: ContentGroup,
  extraData?: Record<string, unknown>
): PageViewBaseOption => {
  const { brand_name } = store.getState().config;
  return {
    event: EVENT,
    pageUrlPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    pageTitle: pageTitle,
    previousPageExists: checkPreviousPageExists(),
    brand: transformBrandForGoogleAnalytics(brand_name),
    siteType: SiteType.Responsive,
    pageType: pageType,
    content_group: content_group || "",
    subID: getSub() || "",
    loginState: getLoginState(),
    ...(extraData && extraData)
  };
};

export const getBaseGAItemProps = ({
  brand,
  product,
  index
}: {
  brand: BRAND_LIST;
  product: ProductGAInfoType;
  index?: number | null;
}): GAeCommerceItem => ({
  item_id: product?.itemId || "",
  item_product_id: product?.productId || "",
  item_l1_id: "",
  item_name: product?.productName || "",
  index: index != null ? index + 1 : 1,
  item_brand: brand || "",
  item_category: product?.displayGender || "", // fallback to gender if category is not available
  item_category2: "",
  item_category3: "",
  item_category4: "",
  item_list_name: "",
  item_list_type: "",
  item_variant: product?.wishColorName?.toLowerCase() || "",
  item_color_code: product?.wishColorCode || "",
  price: product?.displayPrice ?? 0,
  item_size: product?.wishSize?.toLowerCase() || "",
  item_img_id: product?.imageUrl?.split("/").pop() || "",
  item_search_function: "",
  prc_grp_seq_num: product?.priceGroup || "",
  mk_product_id: getCommunicationCodeForGA(product) || ""
});

export const getGAPropsForAddWishlist = (
  brand: BRAND_LIST,
  product: ProductGAInfoType,
  index: number
) => {
  // del item_search_function
  const { item_search_function, ...baseProps } = getBaseGAItemProps({
    brand,
    product,
    index
  });
  return {
    ...baseProps,
    item_oos: ""
  };
};

export const getGAPropsForRemoveWishlist = (
  brand: BRAND_LIST,
  product: ProductGAInfoType,
  index: number
) => {
  // del item_search_function, item_img_id
  const { item_search_function, item_img_id, ...baseProps } =
    getBaseGAItemProps({
      brand,
      product,
      index
    });
  return {
    ...baseProps,
    item_oos: ""
  };
};

export const getCommunicationCodeForGA = (
  product: ProductGAInfoType
): string => {
  const productCode = product?.productId?.split("-")[0] ?? "";
  const colorCode = product?.wishColorCode ?? "";
  const sizeCode = product?.wishSizeCode ?? "";
  const lengthCode = product?.pld?.displayCode ?? "000";
  if (productCode && colorCode && sizeCode && lengthCode) {
    return `${productCode}-${colorCode}-${sizeCode}-${lengthCode}`;
  }
  return "";
};

// Splits a semicolon-delimited tag list into two GA params (tag_list_1 / tag_list_2).
// Splits at the last ";" at or before the GA limit so no facet:value pair is broken mid-string.
export function splitTagList(tagListFull: string): [string, string] {
  if (tagListFull.length <= TAG_LIST_SPLIT_LIMIT) return [tagListFull, ""];
  const cutPoint = tagListFull.lastIndexOf(";", TAG_LIST_SPLIT_LIMIT - 1);
  if (cutPoint === -1)
    return [
      tagListFull.slice(0, TAG_LIST_SPLIT_LIMIT),
      tagListFull.slice(TAG_LIST_SPLIT_LIMIT)
    ];
  return [tagListFull.slice(0, cutPoint + 1), tagListFull.slice(cutPoint + 1)];
}
